import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

import EventsCarousal from './EventsCarousal';
import SubscriptionCarousel from './SubscriptionCarousal';
import OfferCarousel from './OffersCarousal';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db, SECRETS_ENV } from '../../../Config/firebase';
import "swiper/swiper-bundle.css";
import { EventData, OfferType, ServiceProvider, SubscriptionType } from '../types/PartnerTypes';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination'; 
import CosyLoader from './CosyLoader';
import profileImage from '../../../assets/subscriptions/PF_Dummy.jpg';
import ImageCarousal from './ImageCarousal';
import { getCustomBrandingSubdomainFromHost } from '../../../utils/customDomainSubdomain';

export default function PartnerPageComp() {
 
    const [partner, setPartner] = useState<ServiceProvider | null>(null);
    const [offers, setOffers] = useState<OfferType[]>([]);
    const [loading, setLoading] = useState(true);
    const [subscriptions, setSubscriptions] = useState<SubscriptionType[]>([]);
    const [events, setEvents] = useState<EventData[]>([]);
    const [serviceProviderId, setServiceProviderId] = useState<string | null>(null);
    const [customLogo, setCustomLogo] = useState<string | null>(null);
    const [customBrandingImage, setCustomBrandingImage] = useState<string | null>(null);
    const [businessName, setBusinessName] = useState<string | null>(null);
    const [subDomain, setSubDomain] = useState<string>("");
    const navigate = useNavigate();
    const location = useLocation();

    // Optimize the domain check useEffect
    useEffect(() => {
        const host = window.location.host;
        const potentialSubDomain = getCustomBrandingSubdomainFromHost(host);

        if (potentialSubDomain) {
            if (SECRETS_ENV.EXCLUDED_DOMAINS.includes(potentialSubDomain)) {
                // Redirect to /login if the domain is in the excluded list
                navigate('/login');
            } else {
                setSubDomain(potentialSubDomain);
            }
        }
    }, [navigate]);

    // Optimize branding details fetch
    useEffect(() => {
        const fetchBrandingDetails = async () => {
            if (!subDomain) return;

            try {
                const brandingQuery = query(
                    collection(db, 'CustomBranding'), 
                    where('subdomain', '==', `${subDomain}.${SECRETS_ENV.DOMAIN}`)
                );
                const querySnapshot = await getDocs(brandingQuery);
                
                if (!querySnapshot.empty) {
                    const brandingDoc = querySnapshot.docs[0].data();
                    setServiceProviderId(brandingDoc.providerReference);
                    setCustomLogo(brandingDoc.logo || null);
                    setCustomBrandingImage(brandingDoc.brandingImage || null);
                    setBusinessName(brandingDoc.businessName || null);
                } else {
                    // Redirect to /login if no branding details are found
                    navigate('/login');
                }
            } catch (error) {
                console.error("Error fetching branding details:", error);
                // Optionally, redirect to /login on error
                navigate('/login');
            }
        };

        fetchBrandingDetails();
    }, [subDomain, navigate]);

    // Optimize data fetching
    useEffect(() => {
        if (!serviceProviderId) return;
        setLoading(true);

        const unsubscribers: (() => void)[] = [];

        // Fetch partner data
        const fetchPartner = async () => {
            try {
                const docRef = doc(db, 'ServiceProviders', serviceProviderId);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                    const data = docSnap.data() as ServiceProvider;
                    setPartner({
                        ...data,
                        id: docSnap.id,
                        created_time: data.created_time,
                        edited_time: data.edited_time,
                        plundrDescription: data.plundrDescription || "",
                        plundrImages: data.plundrImages || []
                    });
                }
            } catch (error) {
                console.error('Error fetching partner:', error);
            } finally {
                setLoading(false);
            }
        };

        // Combine all collection listeners
        const setupCollectionListeners = () => {
            // Offers listener
            const offersUnsubscribe = onSnapshot(
                collection(db, 'ServiceProviders', serviceProviderId, 'Offers'),
                (snapshot) => {
                    const offersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as OfferType[];
                    setOffers(offersData);
                }
            );
            unsubscribers.push(offersUnsubscribe);

            // Subscriptions listener
            const subscriptionsUnsubscribe = onSnapshot(
                collection(db, `ServiceProviders/${serviceProviderId}/Subscriptions`),
                (snapshot) => {
                    const subscriptionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SubscriptionType[];
                    setSubscriptions(subscriptionsData);
                }
            );
            unsubscribers.push(subscriptionsUnsubscribe);

            // Events listener
            const eventsUnsubscribe = onSnapshot(
                collection(db, 'ServiceProviders', serviceProviderId, 'Events'),
                (snapshot) => {
                    const eventsData = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }) as EventData);
                    setEvents(eventsData);
                }
            );
            unsubscribers.push(eventsUnsubscribe);
        };

        fetchPartner();
        setupCollectionListeners();

        // Cleanup
        return () => {
            unsubscribers.forEach(unsubscribe => unsubscribe());
        };
    }, [serviceProviderId]);

    // Add this useEffect inside the PartnerPageComp component, alongside other useEffects
    useEffect(() => {
        document.title = partner?.businessProfile?.businessName || 'HoSaksham';
    }, [partner]);

    // Fallback Components
    if (loading) {
        return (
            <div className='bg-white h-[100vh] w-full flex justify-center items-center'>
                <div
                    className="loading absolute top-[60%] left-[50%] flex flex-col gap-24 items-center -translate-x-[50%] -translate-y-[50%]">

                    <CosyLoader />
                    <div>
                        Loading...
                    </div>

                </div>
            </div>

        );
    }

    if (!partner) {
        return (
            <div className='bg-white h-[100vh] w-full flex justify-center items-center'>
                <div
                    className="loading absolute top-[50%] left-[50%] flex flex-col gap-4 items-center -translate-x-[50%] -translate-y-[50%]">


                    <div>
                        Parner Not Found! Please check the URL and try again.
                    </div>
                    <div>
                        <button onClick={() => navigate("/mydashboard")}
                            className="rounded-xl border-2 border-[#1D4ED8] border-solid px-6 py-2 font-medium text-[#1D4ED8] hover:bg-[#1D4ED8] hover:text-white"
                            style={{ borderColor: 'cc_brand' }}>Back to Home!
                        </button>
                    </div>

                </div>
            </div>

        );
    }

    // Header JSX with custom logo
    const renderHeader = () => (
        <header className="sticky z-50 top-0 mb-1 px-4 bg-white drop-shadow-lg">
            <div className="mx-auto flex max-w-screen-xl items-center justify-between py-2 sm:py-4">
                <a className="flex flex-col items-start font-bold">
                    {partner?.businessProfile?.businessName}
                </a>
                {/* <button
                    onClick={() => navigate('/login')}
                    className="rounded-xl border-2 border-[#1D4ED8] border-solid px-4 py-2 font-medium text-[#1D4ED8] hover:bg-[#1D4ED8] hover:text-white"
                >
                    Login
                </button> */}
            </div>
        </header>
    );

    // Footer JSX with custom business name and links
    const renderFooter = () => (
        <footer className="bg-gray-10 mt-10 sm:mt-20 w-full">
            <div className="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="text-center flex flex-col gap-4">
                    <p className="text-base text-black">
                        © {new Date().getFullYear()} {partner?.businessProfile?.businessName} | 
                        All Rights Reserved | Trusted Partner of Hosaksham
                    </p>
                    <div className="flex justify-center gap-4 text-sm text-gray-600">
                        <a 
                            href="https://www.hosaksham.com/privacy-policy" 
                            className="hover:text-[#1D4ED8] hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Privacy Policy
                        </a>
                        <span>|</span>
                        <a 
                            href="https://www.hosaksham.com/terms-and-conditions" 
                            className="hover:text-[#1D4ED8] hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Terms & Conditions
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );

    return (
        <>
            {renderHeader()}
            <div>
                <div className="mx-auto bg-white max-w-screen-xl">
                    {/* Banner */}
                    <div className="relative h-18 sm:h-44 rounded-b-lg bg-cover bg-center bg-no-repeat shadow-xl drop-shadow-lg" style={{ zIndex: 1 }}>
                        <svg className="absolute bottom-0" style={{ zIndex: 0 }} viewBox="0 0 1425 205.55" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <g>
                                <path
                                    d="M0,17.27l23.75,5.09C47.5,27.38,95,37.57,142.5,39.47s95-4.36,142.5-10.28,95-11.35,142.5-7.4,95,17.43,142.5,24,95,6.25,142.5,5.83,95-.9,142.5-3S950,43,997.5,42.92s95,3.12,142.5,2.88,95-4.11,142.5-3.62,95,5.51,118.75,8L1425,52.62v170.2H0Z"
                                    fill="#FFCAD7"></path>
                                <path
                                    d="M0,94.56l23.75-6.17C47.5,82.22,95,69.89,142.5,65.2s95-1.64,142.5,4.85S380,86.33,427.5,86.5,522.5,77,570,73.59s95-.66,142.5,1.81,95,4.6,142.5,3.78,95-4.6,142.5-6.58,95-1.89,142.5,1.24,95,9.45,142.5,13.15,95,4.85,118.75,5.35l23.75.57V222.82H0Z"
                                    fill="#FF7D9D"></path>
                                <path
                                    d="M0,134,23.75,132C47.5,129.91,95,125.8,142.5,125.39s95,2.88,142.5,5.75,95,5.35,142.5,4,95-6.58,142.5-8.06,95,.66,142.5-.41,95-5.51,142.5-9.21,95-6.66,142.5-5.83,95,5.42,142.5,9.86,95,8.47,142.5,9.05,95-2.47,118.75-4L1425,125v97.84H0Z"
                                    fill="#FF3366"></path>
                                <path
                                    d="M0,178.42l23.75-1.64c23.75-1.65,71.25-4.94,118.75-5.59s95,1.23,142.5,2.71,95,2.63,142.5,4.11,95,3.45,142.5,2.88,95-3.54,142.5-8.8,95-12.58,142.5-13.57,95,4.53,142.5,8.23,95,5.67,142.5,4,95-6.91,142.5-9.79,95-3.37,118.75-3.7L1425,157v65.78H0Z"
                                    fill="#27272a"></path>
                            </g>
                            <defs>
                                <clipPath id="clip0">
                                    <rect width="1425" height="444" fill="white"></rect>
                                </clipPath>
                            </defs>
                        </svg>
                        <div className="px-4 pt-8 pb-10">
                            <div
                                className="absolute overflow-hidden inset-x-0 -bottom-10 mx-auto w-24 h-24 sm:w-36 sm:h-36 rounded-full border-4 sm:border-8 border-white shadow-lg">

                                <img className="mx-auto w-full h-full object-cover rounded-full"
                                    src={partner?.photo_url || profileImage} alt="Profile Image" />
                            </div>

                        </div>
                    </div>
                    <div>
                    </div>


                    <div className='flex flex-col sm:flex-row  items-center '>


                    <div
                        className={`mt-10 flex  flex-col items-start justify-center space-y-4 py-8 px-4 sm:space-y-4 md:justify-between lg:px-4 w-full ${partner?.plundrImages?.length > 0 ? "sm:w-[70%]" : "sm:w-[100%]"}`}>
                        <div className="w-full">
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{partner?.businessProfile?.businessName}</h1>
                            <p className="mt-2 w-full text-sm sm:text-base text-gray-600">
                                {partner?.plundrDescription || `${partner?.businessProfile?.businessName} is a place where you can find all the services like ${partner?.ServiceTypes?.join(", ")}. We are a team of professionals who are dedicated to providing you with the best services possible.`}
                            </p>
                            <div className='flex mt-4 gap-2 flex-wrap'>
                                {partner?.ServiceTypes?.map((item) => (
                                    <div 
                                        key={item}
                                        className='text-white bg-[#1D4ED8] cursor-pointer hover:shadow-md px-2 sm:px-3.5 py-1 font-semibold rounded-2xl text-sm'
                                    >
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col  items-start">
                            {/* <button
                                className="whitespace-nowrap flex items-center rounded-lg text-sm sm:text-base bg-pink-600 px-3 font-semibold sm:px-6 py-1.5 sm:py-2 sm:font-bold text-white transition hover:translate-y-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 inline h-6 w-6" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                Chat with us
                            </button> */}
                            <p className="mt-2 flex gap-2 text-sm sm:text-base items-center whitespace-nowrap text-gray-500 sm:justify-start">
                                <EmailIcon className='text-sm sm:text-base'/>
                                {partner?.email}
                            </p>
                            <p className="mt-2 flex gap-2 text-sm sm:text-base items-center whitespace-nowrap text-gray-500 sm:justify-start">
                                <PhoneIcon className='text-sm sm:text-base'/>
                                {partner?.phone_number}
                            </p>

                        </div>

                    </div>
                    <div className={`h-[100%] w-[100%] sm:ml-16 ${partner?.plundrImages?.length > 0 ? "sm:w-[100%]" : "sm:w-[0%]"}`}>
                        <ImageCarousal images={partner?.plundrImages}/>
                    </div>
                </div>

                    <main className='px-4 mt-4'>

                        {events.length > 0 && offers.length > 0 ? (
                            <>
                                <div className='mt-4'>
                                    <EventsCarousal events={events} partnerId={serviceProviderId || ''} />
                                </div>
                                <div>
                                    <OfferCarousel offers={offers} partnerId={serviceProviderId || ''}  />
                                </div>
                                <div>
                                    <SubscriptionCarousel subscriptions={subscriptions} partnerId={serviceProviderId || ''} right={false} />
                                </div>
                            </>
                        ) : events.length > 0 && offers.length === 0 ? (
                            <>
                                <div className='mt-4'>
                                    <EventsCarousal events={events} partnerId={serviceProviderId || ''} />
                                </div>
                                <div>
                                    <SubscriptionCarousel subscriptions={subscriptions} partnerId={serviceProviderId || ''} />
                                </div>
                                <div>
                                    <OfferCarousel offers={offers} partnerId={serviceProviderId || ''} />
                                </div>
                            </>
                        ) : events.length === 0 && offers.length > 0 ? (
                            <>
                                <div className='mt-4'>
                                    <OfferCarousel offers={offers} partnerId={serviceProviderId || ''} />
                                </div>
                                <div>
                                    <SubscriptionCarousel subscriptions={subscriptions} partnerId={serviceProviderId || ''} />
                                </div>
                                <div>
                                    <EventsCarousal events={events} partnerId={serviceProviderId || ''} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='mt-4'>
                                    <SubscriptionCarousel subscriptions={subscriptions} partnerId={serviceProviderId || ''} right={false} />
                                </div>
                                <div>
                                    <OfferCarousel offers={offers} partnerId={serviceProviderId || ''} />
                                </div>
                                <div>
                                    <EventsCarousal events={events} partnerId={serviceProviderId || ''} />
                                </div>
                            </>
                        )}

                    </main>

                </div>

            </div>
            {renderFooter()}
        </>
    )
}